<template>
  <div class="demand-view">
    <div class="container">
      <div class="main-content">
        <!-- 左侧面板 -->
        <div class="panel left-panel">
          <!-- 合并后的模型参数卡片 -->
          <div class="card parameter-card">
            <div class="card-header">
              <i class="fas fa-sliders-h"></i>
              <h3>情景选择</h3>
              <!-- 添加上传Excel按钮 -->
              <div class="upload-btn-container">
                <input
                  type="file"
                  ref="fileInput"
                  accept=".xlsx"
                  style="display: none"
                  @change="handleFileUpload"
                />
                <button @click="triggerFileUpload" class="btn btn-primary upload-btn">
                  <i class="fas fa-upload"></i> 上传Excel
                </button>
              </div>
            </div>
            <div class="card-body">
              <div class="parameters-row">
                <!-- 情景选择部分 -->
                <div class="parameter-group">
                  <select v-model="selectedScenario" class="form-select">
                    <option value="baseline">碳中和情景</option>
                    <option v-for="scenario in customScenarios" 
                            :key="scenario"
                            :value="scenario">
                      {{ scenario }}
                    </option>
                  </select>
                </div>

                <!-- 省份选择部分 -->
                <div class="parameter-group">
                  <select v-model="selectedProvince" class="form-select">
                    <option value="">选择省份</option>
                    <option v-for="province in provinces" 
                            :key="province" 
                            :value="province">
                      {{ province }}
                    </option>
                  </select>
                </div>

                <!-- 年份选择 -->
                <div class="parameter-group">
                  <select v-model="selectedYear" class="form-select">
                    <option v-for="year in years" 
                            :key="year" 
                            :value="year.toString()">
                      {{ year }}
                    </option>
                  </select>
                </div>

                <!-- 图表类型选择 -->
                <div class="parameter-group">
                  <select v-model="chartType" class="form-select">
                    <option value="line">折线图</option>
                    <option value="bar">条形图</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <!-- 数据编辑卡片 -->
          <div class="card edit-card" v-if="selectedProvince && selectedYear">
            <div class="card-header">
              <i class="fas fa-edit"></i>
              <h3>模型参数</h3>
            </div>
            <div class="card-body">
              <div class="edit-form">
                <div class="data-section">
                  <div class="basic-data-grid">
                    <div class="form-group">
                      <label>人口 (万人)</label>
                      <input type="number" 
                             v-model="currentData.pop" 
                             @change="handleDataChange('pop')"
                             class="form-input">
                    </div>
                    <div class="form-group">
                      <label>GDP (亿元)</label>
                      <input type="number" 
                             v-model="currentData.gdp" 
                             @change="handleDataChange('gdp')"
                             class="form-input">
                    </div>
                  </div>

                  <!-- 添加产业增加值部分 -->
                  <div class="industry-increment">
                    <div class="increment-row">
                      <label class="increment-label">增加值（亿元）：</label>
                      <div class="increment-inputs">
                        <div class="increment-input-group">
                          <label>一产</label>
                          <input type="number"
                                 v-model="currentData.primaryIndustry"
                                 @change="handleDataChange('primaryIndustry')"
                                 class="form-input">
                        </div>
                        <div class="increment-input-group">
                          <label>二产</label>
                          <input type="number"
                                 v-model="currentData.secondaryIndustry"
                                 @change="handleDataChange('secondaryIndustry')"
                                 class="form-input">
                        </div>
                        <div class="increment-input-group">
                          <label>三产</label>
                          <input type="number"
                                 v-model="currentData.tertiaryIndustry"
                                 @change="handleDataChange('tertiaryIndustry')"
                                 class="form-input">
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="sectors-grid">
                    <!-- 左侧三个部门 -->
                    <div class="sectors-column left-sectors">
                      <!-- 农业 -->
                      <div v-if="'agriculture' in currentData.sectors" class="sector-group">
                        <div class="sector-header">
                          <h5>{{ getSectorLabel('agriculture') }}</h5>
                        </div>
                        <div class="sector-inputs">
                          <div class="input-group" style="gap: 4px">
                            <label>能耗强度</label>
                            <input type="number" 
                                   v-model="currentData.sectors.agriculture.intensity" 
                                   @change="handleSectorChange('agriculture', 'intensity')"
                                   class="form-input">
                            <span class="unit">吨标煤 / 亿元</span>
                          </div>
                          <div class="input-group" style="gap: 4px">
                            <label>电气化率</label>
                            <input type="number" 
                                   v-model="currentData.sectors.agriculture.electrification" 
                                   @change="handleSectorChange('agriculture', 'electrification')"
                                   class="form-input">
                            <span class="unit">%</span>
                          </div>
                          <div class="input-group" style="gap: 4px">
                            <label>氢能化率</label>
                            <input type="number" 
                                   v-model="currentData.sectors.agriculture.hydrogenation" 
                                   @change="handleSectorChange('agriculture', 'hydrogenation')"
                                   class="form-input">
                            <span class="unit">%</span>
                          </div>
                        </div>
                      </div>

                      <!-- 工业 -->
                      <div v-if="'industry' in currentData.sectors" class="sector-group">
                        <div class="sector-header">
                          <h5>{{ getSectorLabel('industry') }}</h5>
                        </div>
                        <div class="sector-inputs">
                          <div class="input-group" style="gap: 4px">
                            <label>能耗强度</label>
                            <input type="number" 
                                   v-model="currentData.sectors.industry.intensity" 
                                   @change="handleSectorChange('industry', 'intensity')"
                                   class="form-input">
                            <span class="unit">吨标煤 / 亿元</span>
                          </div>
                          <div class="input-group" style="gap: 4px">
                            <label>电气化率</label>
                            <input type="number" 
                                   v-model="currentData.sectors.industry.electrification" 
                                   @change="handleSectorChange('industry', 'electrification')"
                                   class="form-input">
                            <span class="unit">%</span>
                          </div>
                          <div class="input-group" style="gap: 4px">
                            <label>氢能化率</label>
                            <input type="number" 
                                   v-model="currentData.sectors.industry.hydrogenation" 
                                   @change="handleSectorChange('industry', 'hydrogenation')"
                                   class="form-input">
                            <span class="unit">%</span>
                          </div>
                        </div>
                      </div>

                      <!-- 建筑业 -->
                      <div v-if="'construction' in currentData.sectors" class="sector-group">
                        <div class="sector-header">
                          <h5>{{ getSectorLabel('construction') }}</h5>
                        </div>
                        <div class="sector-inputs">
                          <div class="input-group" style="gap: 4px">
                            <label>能耗强度</label>
                            <input type="number" 
                                   v-model="currentData.sectors.construction.intensity" 
                                   @change="handleSectorChange('construction', 'intensity')"
                                   class="form-input">
                            <span class="unit">吨标煤 / 亿元</span>
                          </div>
                          <div class="input-group" style="gap: 4px">
                            <label>电气化率</label>
                            <input type="number" 
                                   v-model="currentData.sectors.construction.electrification" 
                                   @change="handleSectorChange('construction', 'electrification')"
                                   class="form-input">
                            <span class="unit">%</span>
                          </div>
                          <div class="input-group" style="gap: 4px">
                            <label>氢能化率</label>
                            <input type="number" 
                                   v-model="currentData.sectors.construction.hydrogenation" 
                                   @change="handleSectorChange('construction', 'hydrogenation')"
                                   class="form-input">
                            <span class="unit">%</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- 右侧三个部门 -->
                    <div class="sectors-column right-sectors">
                      <!-- 交通运输 -->
                      <div v-if="'transport' in currentData.sectors" class="sector-group">
                        <div class="sector-header">
                          <h5>{{ getSectorLabel('transport') }}</h5>
                        </div>
                        <div class="sector-inputs">
                          <div class="input-group" style="gap: 4px">
                            <label>能耗强度</label>
                            <input type="number" 
                                   v-model="currentData.sectors.transport.intensity" 
                                   @change="handleSectorChange('transport', 'intensity')"
                                   class="form-input">
                            <span class="unit">吨标煤 /  亿吨公里</span>
                          </div>
                          <div class="input-group" style="gap: 4px">
                            <label>电气化率</label>
                            <input type="number" 
                                   v-model="currentData.sectors.transport.electrification" 
                                   @change="handleSectorChange('transport', 'electrification')"
                                   class="form-input">
                            <span class="unit">%</span>
                          </div>
                          <div class="input-group" style="gap: 4px">
                            <label>氢能化率</label>
                            <input type="number" 
                                   v-model="currentData.sectors.transport.hydrogenation" 
                                   @change="handleSectorChange('transport', 'hydrogenation')"
                                   class="form-input">
                            <span class="unit">%</span>
                          </div>
                        </div>
                      </div>

                      <!-- 服务业 -->
                      <div v-if="'service' in currentData.sectors" class="sector-group">
                        <div class="sector-header">
                          <h5>{{ getSectorLabel('service') }}</h5>
                        </div>
                        <div class="sector-inputs">
                          <div class="input-group" style="gap: 4px">
                            <label>能耗强度</label>
                            <input type="number" 
                                   v-model="currentData.sectors.service.intensity" 
                                   @change="handleSectorChange('service', 'intensity')"
                                   class="form-input">
                            <span class="unit">吨标煤 / 亿元</span>
                          </div>
                          <div class="input-group" style="gap: 4px">
                            <label>电气化率</label>
                            <input type="number" 
                                   v-model="currentData.sectors.service.electrification" 
                                   @change="handleSectorChange('service', 'electrification')"
                                   class="form-input">
                            <span class="unit">%</span>
                          </div>
                          <div class="input-group" style="gap: 4px">
                            <label>氢能化率</label>
                            <input type="number" 
                                   v-model="currentData.sectors.service.hydrogenation" 
                                   @change="handleSectorChange('service', 'hydrogenation')"
                                   class="form-input">
                            <span class="unit">%</span>
                          </div>
                        </div>
                      </div>

                      <!-- 居民生活 -->
                      <div v-if="'residential' in currentData.sectors" class="sector-group">
                        <div class="sector-header">
                          <h5>{{ getSectorLabel('residential') }}</h5>
                        </div>
                        <div class="sector-inputs">
                          <div class="input-group" style="gap: 4px">
                            <label>能耗强度</label>
                            <input type="number" 
                                   v-model="currentData.sectors.residential.intensity" 
                                   @change="handleSectorChange('residential', 'intensity')"
                                   class="form-input">
                            <span class="unit">吨标煤 / 万人</span>
                          </div>
                          <div class="input-group" style="gap: 4px">
                            <label>电气化率</label>
                            <input type="number" 
                                   v-model="currentData.sectors.residential.electrification" 
                                   @change="handleSectorChange('residential', 'electrification')"
                                   class="form-input">
                            <span class="unit">%</span>
                          </div>
                          <div class="input-group" style="gap: 4px">
                            <label>氢能化率</label>
                            <input type="number" 
                                   v-model="currentData.sectors.residential.hydrogenation" 
                                   @change="handleSectorChange('residential', 'hydrogenation')"
                                   class="form-input">
                            <span class="unit">%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="selectedScenario !== 'baseline'" class="action-group">
                    <button class="btn btn-success save-btn" @click="saveChanges">
                      <i class="fas fa-save"></i> 保存修改
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 右侧图表面板 -->
        <div class="panel right-panel fixed-height">
          <div class="card chart-card">
            <div class="card-body">
              <div class="charts-grid">
                <div class="chart-item">
                  <div ref="popGdpChartRef" class="chart-container"></div>
                </div>
                <div class="chart-item">
                  <div ref="industryStructureChartRef" class="chart-container"></div>
                </div>
                <div class="chart-item">
                  <div ref="intensityChartRef" class="chart-container"></div>
                </div>
                <div class="chart-item">
                  <div ref="electrificationChartRef" class="chart-container"></div>
                </div>
                <div class="chart-item">
                  <div ref="hydrogenationChartRef" class="chart-container"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import PageHeader from '@/components/PageHeader.vue'
import * as echarts from 'echarts'
import { ElSelect, ElOption } from 'element-plus'
import axios from 'axios'
import request from '@/utils/request'

export default {
  name: 'DemandView',
  components: {
    ElSelect,
    ElOption,
    PageHeader
  },
  setup() {
    const store = useStore()
    
    // 基础数据定义
    const selectedScenario = ref('baseline')
    const selectedProvince = ref('')
    const selectedYear = ref('2020')
    const selectedVariable = ref('pop')
    const chartType = ref('line')
    const showScenarioCreate = ref(false)
    const newScenarioName = ref('')
    const currentData = ref({
      pop: 0,
      gdp: 0,
      sectors: {
        industry: {
          intensity: 0,
          electrification: 0,
          hydrogenation: 0
        },
        transport: {
          intensity: 0,
          electrification: 0,
          hydrogenation: 0
        },
        agriculture: {
          intensity: 0,
          electrification: 0,
          hydrogenation: 0
        },
        construction: {
          intensity: 0,
          electrification: 0,
          hydrogenation: 0
        },
        service: {
          intensity: 0,
          electrification: 0,
          hydrogenation: 0
        },
        residential: {
          intensity: 0,
          electrification: 0,
          hydrogenation: 0
        }
      }
    })

    // 文件上传相关
    const fileInput = ref(null)
    const isUploading = ref(false)
    const uploadStatus = ref('')

    // 触发文件选择
    const triggerFileUpload = () => {
      fileInput.value.click()
    }

    // 处理文件上传
    const handleFileUpload = async (event) => {
      const file = event.target.files[0]
      if (!file) return
      
      // 创建FormData对象
      const formData = new FormData()
      formData.append('file', file)
      
      isUploading.value = true
      uploadStatus.value = '上传中...'
      
      try {
        console.log('开始上传文件...')
        // 发送文件到后端，使用request工具
        const response = await request({
          url: '/api/upload-2025-excel',  // 添加/api前缀
          method: 'post',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        
        console.log('上传响应:', response)
        
        // 处理响应
        if (response.success) {
          uploadStatus.value = '上传成功！'
          alert('Excel文件已上传并处理成功！')
          
          // 刷新页面以加载新数据
          window.location.reload()
        } else {
          uploadStatus.value = '上传失败'
          alert(`处理失败: ${response.error}`)
        }
      } catch (error) {
        console.error('上传失败:', error)
        uploadStatus.value = '上传失败'
        alert('文件上传或处理失败，请检查控制台日志')
      } finally {
        isUploading.value = false
        // 清除文件输入，允许重新选择同一文件
        event.target.value = null
      }
    }

    // 省份列表
    const provinces = [
      '北京', '天津', '河北', '山西', '内蒙古', '辽宁', '吉林', '黑龙江',
      '上海', '江苏', '浙江', '安徽', '福建', '江西', '山东', '河南',
      '湖北', '湖南', '广东', '广西', '海南', '重庆', '四川', '贵州',
      '云南', '陕西', '甘肃', '青海', '宁夏', '新疆'
    ]

    // 年份列表
    const years = [
      2020, 2025, 2030, 2035, 2040, 2045, 2050, 2055, 2060
    ]

    // 部门映射
    const sectorLabels = {
      industry: '工业',
      transport: '交通运输',
      agriculture: '农业',
      construction: '建筑业',
      service: '服务业',
      residential: '居民生活'
    }

    // 获取自定义情景列表
    const customScenarios = computed(() => {
      const scenarios = store.state.scenarios
      if (!scenarios) return []
      // 过滤掉 '激进情景' 和 '缓慢情景'
      return Object.keys(scenarios)
        .filter(s => s !== 'baseline' && s !== '激进情景' && s !== '缓慢场景')
    })

    // 监听选择变化
    watch([selectedScenario, selectedProvince, selectedYear], 
      ([scenario, province, year]) => {
        console.log('Selection changed:', scenario, province, year)
        if (scenario && province && year) {
          loadData(scenario, province, year)
        }
    }, { immediate: true })

    // 监听 currentData 变化
    watch(() => store.state.currentData, (newData) => {
      console.log('Current data updated:', newData)
      if (newData) {
        currentData.value = JSON.parse(JSON.stringify(newData))
      }
    }, { deep: true })

    // 加载数据
    function loadData(scenario, province, year) {
      console.log('Loading data for:', scenario, province, year)
      store.dispatch('loadProvinceData', {
        scenario,
        province,
        year: year.toString()
      }).then(() => {
        // 数据加载完成后更新图表
        if (chart) {
          updateChart()
        }
      }).catch(error => {
        console.error('Failed to load data:', error)
        alert('数据加载失败')
      })
    }

    // 创建新情景
    function createScenario() {
      if (!newScenarioName.value) {
        alert('请输入情景名称')
        return
      }

      // 检查情景名称是否已存在
      if (store.state.scenarios[newScenarioName.value]) {
        alert('情景名称已存在')
        return
      }

      store.dispatch('createNewScenario', {
        name: newScenarioName.value
      })

      newScenarioName.value = ''
      showScenarioCreate.value = false
    }

    // 删除情景
    function deleteScenario() {
      if (confirm('确定要删除该情景吗？')) {
        store.dispatch('deleteScenario', selectedScenario.value)
        selectedScenario.value = 'baseline'
      }
    }

    // 处理数据变化
    function handleDataChange(field) {
      const value = Number(currentData.value[field])
      if (isNaN(value)) {
        alert('请输入有效数字')
        return
      }

      const scenario = selectedScenario.value
      const province = selectedProvince.value
      const year = selectedYear.value

      // 直接更新 scenarios 中的数据以实现图表联动
      if (store.state.scenarios[scenario] && store.state.scenarios[scenario][province]) {
        if (field === 'pop' || field === 'gdp') {
          store.state.scenarios[scenario][province][field][year] = value
        } else if (field === 'primaryIndustry') {
          store.state.scenarios[scenario][province].gdp_composition.primary[year] = value
        } else if (field === 'secondaryIndustry') {
          store.state.scenarios[scenario][province].gdp_composition.secondary[year] = value
        } else if (field === 'tertiaryIndustry') {
          store.state.scenarios[scenario][province].gdp_composition.tertiary[year] = value
        }
      }

      // 更新当前数据
      store.commit('SET_CURRENT_DATA', {
        ...store.state.currentData,
        [field]: value
      })
      
      // 实时更新图表
      nextTick(() => {
        updateAllCharts()
      })
    }

    // 处理门数据变化
    function handleSectorChange(sectorName, field) {
      const value = Number(currentData.value.sectors[sectorName][field])
      if (isNaN(value)) {
        alert('请输入有效数字')
        return
      }

      const scenario = selectedScenario.value
      const province = selectedProvince.value
      const year = selectedYear.value

      // 直接更新 scenarios 中的数据以实现图表联动
      if (store.state.scenarios[scenario] && store.state.scenarios[scenario][province]) {
        if (store.state.scenarios[scenario][province].sectors[sectorName] &&
            store.state.scenarios[scenario][province].sectors[sectorName][field]) {
          store.state.scenarios[scenario][province].sectors[sectorName][field][year] = value
        }
      }
      
      // 更新当前数据
      const newData = JSON.parse(JSON.stringify(store.state.currentData))
      newData.sectors[sectorName][field] = value
      store.commit('SET_CURRENT_DATA', newData)

      // 实时更新图表
      nextTick(() => {
        updateAllCharts()
      })
    }

    // 保存修改
    const saveChanges = async () => {
      try {
        await store.dispatch('saveChanges')
        // 保存成功后更新图表
        updateChart()
        alert('数据保存成功！')
      } catch (error) {
        alert('保存失败：' + error.message)
      }
    }

    // 取部门中文名
    function getSectorLabel(sectorName) {
      return sectorLabels[sectorName] || sectorName
    }

    const chartRef = ref(null)
    let chart = null

    // 获取图表标题
    const getChartTitle = (variable) => {
      const titles = {
        gdp: `${selectedScenario.value === 'baseline' ? '碳中和情景' : selectedScenario.value}：${selectedProvince.value}GDP变化`,
        pop: `${selectedScenario.value === 'baseline' ? '碳中和情景' : selectedScenario.value}：${selectedProvince.value}人口变化`,
        intensity: `${selectedScenario.value === 'baseline' ? '碳中和情景' : selectedScenario.value}：${selectedProvince.value}分部门能源强度变化`,
        electrification: `${selectedScenario.value === 'baseline' ? '碳中和情景' : selectedScenario.value}：${selectedProvince.value}分部门电气化率变化`,
        hydrogenation: `${selectedScenario.value === 'baseline' ? '碳中和情景' : selectedScenario.value}：${selectedProvince.value}分部门氢能化率变化`
      }
      return titles[variable]
    }

    // 获取Y轴名称
    const getYAxisName = (variable) => {
      const names = {
        gdp: 'GDP',
        pop: '人口',
        intensity: '能源强度',
        electrification: '电气化率',
        hydrogenation: '氢能化率'
      }
      return names[variable]
    }

    // 获取Y轴单位
    const getYAxisUnit = (variable) => {
      const units = {
        gdp: '亿元',
        pop: '万人',
        intensity: '吨标煤/亿元',
        electrification: '%',
        hydrogenation: '%'
      }
      return units[variable]
    }

    // 获取图表数据
    const getChartData = (variable, scenario, province) => {
      const scenarioData = store.state.scenarios[scenario]
      if (!scenarioData || !scenarioData[province]) return []

      const years = ['2020', '2025', '2030', '2035', '2040', '2045', '2050', '2055', '2060']
      
      switch(variable) {
        case 'gdp':
        case 'pop':
          // 单数据系列，只返回一图例
          return [{
            name: getYAxisName(variable), // 使用变量名作为图例名
            data: years.map(year => 
              variable === 'gdp' ? 
              scenarioData[province].gdp[year] || 0 :
              scenarioData[province].pop[year] || 0
            )
          }]
        case 'intensity':
        case 'electrification':
        case 'hydrogenation': {
          // 多数据系列，返回多个图例
          const sectors = Object.keys(scenarioData[province].sectors)
          return sectors.map(sector => ({
            name: sectorLabels[sector],
            data: years.map(year => 
              scenarioData[province].sectors[sector][variable][year] || 0
            )
          }))
        }
        default:
          return []
      }
    }

    // 修改 updateChart 函数
    const updateChart = () => {
      if (!chart) return

      const seriesData = getChartData(selectedVariable.value, selectedScenario.value, selectedProvince.value)
      
      const option = {
        title: {
          text: getChartTitle(selectedVariable.value),
          left: 'center'
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: function(params) {
            let result = `<div style="font-size:16px;font-weight:bold;margin-bottom:8px">${params[0].axisValue}年</div>`
            params.forEach(param => {
              result += `<div style="margin:4px 0">
                <span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${param.color};"></span>
                <span style="font-size:14px">${param.seriesName}: ${param.value}${getYAxisUnit(selectedVariable.value)}</span>
              </div>`
            })
            return result
          },
          textStyle: {
            fontSize: 14
          }
        },
        legend: {
          top: '30px',
          type: 'scroll',
          data: seriesData.map(item => item.name) // 确保图例与数据系列匹配
        },
        grid: {
          left: '5%',
          right: '5%',
          bottom: '10%',
          top: '80px',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: ['2020', '2025', '2030', '2035', '2040', '2045', '2050', '2055', '2060'],
          name: '年份'
        },
        yAxis: {
          type: 'value',
          name: `${getYAxisName(selectedVariable.value)}（${getYAxisUnit(selectedVariable.value)}）`,
          axisLabel: {
            formatter: '{value}'
          }
        },
        series: seriesData.map(item => ({
          name: item.name,
          data: item.data,
          type: chartType.value,
          smooth: chartType.value === 'line',
          symbol: chartType.value === 'line' ? 'circle' : 'none',
          symbolSize: 6,
        }))
      }
      
      chart.setOption(option, true)
      nextTick(() => {
        chart.resize()
      })
    }

    // 修改 watch 监听器，确保图表更新
    watch([selectedVariable, selectedScenario, selectedProvince, chartType], () => {
      if (selectedProvince.value && selectedScenario.value && selectedYear.value) {
        nextTick(() => {
          updateChart()
        })
      }
    }, { deep: true })

    // 初始化图表
    const initChart = () => {
      if (chartRef.value) {
        chart = echarts.init(chartRef.value)
        window.addEventListener('resize', () => {
          chart.resize()
        })
        updateChart()
      }
    }

    // 修改组件挂载时的初始化逻辑
    onMounted(() => {
      // 按顺序设置初始值
      selectedYear.value = '2020'
      selectedScenario.value = 'baseline'
      selectedProvince.value = '北京'
      selectedVariable.value = 'pop'
      
      // 等待数据加载完成后再初始化图表
      nextTick(() => {
        initChart()
        // 确保初始化后立即更新图表
        if (selectedProvince.value && selectedScenario.value && selectedYear.value) {
          updateChart()
        }
      })
    })

    // 在 setup 中添加新的图表引用
    const popGdpChartRef = ref(null)
    const intensityChartRef = ref(null)
    const electrificationChartRef = ref(null)
    const hydrogenationChartRef = ref(null)
    let popGdpChart = null
    let intensityChart = null
    let electrificationChart = null
    let hydrogenationChart = null

 

    // 更新所有图表的方法
    const updateAllCharts = () => {
      if (selectedProvince.value && selectedScenario.value) {
        updatePopGdpChart()
        updateIndustryStructureChart()
        updateIntensityChart()
        updateElectrificationChart()
        updateHydrogenationChart()
      }
    }

    // 添加各个图表的更新方法
    const updatePopGdpChart = () => {
      const option = {
        title: { 
          text: '人口/GDP', 
          left: 'center',
          textStyle: {
            fontSize: 24,
            fontWeight: 'bold'
          }
        },
        tooltip: { 
          trigger: 'axis',
          textStyle: {
            fontSize: 16
          }
        },
        legend: { 
          bottom: 10,  // 图例放到底部
          itemGap: 30,
          textStyle: {
            fontSize: 20
          }
        },
        grid: {
          top: 60,
          bottom: 40,  // 为底部图例留出空间
          left: 60,
          right: 60,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: years,
          name: '',
          nameTextStyle: {
            fontSize: 20
          },
          axisLabel: {
            fontSize: 20  // 增大X轴刻度标签字体
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '(亿元)         ',
            position: 'left',
            nameTextStyle: {
              fontSize: 20  // 增大Y轴名称字体
            },
            axisLabel: {
              fontSize: 20  // 增大Y轴刻度标签字体
            }
          },
          {
            type: 'value',
            name: '          (万人)', 
            position: 'right',
            nameTextStyle: {
              fontSize: 20  // 增大Y轴名称字体
            },
            axisLabel: {
              fontSize: 20  // 增大Y轴刻度标签字体
            }
          }
        ],
        series: [
          {
            name: 'GDP',
            type: chartType.value,
            data: years.map(year => store.state.scenarios[selectedScenario.value][selectedProvince.value].gdp[year] || 0),
            yAxisIndex: 0
          },
          {
            name: '人口',
            type: chartType.value,
            data: years.map(year => store.state.scenarios[selectedScenario.value][selectedProvince.value].pop[year] || 0),
            yAxisIndex: 1
          }
        ]
      }
      popGdpChart?.setOption(option)
    }

    const updateIntensityChart = () => {
      // 获取所有部门
      const sectors = ['agriculture', 'industry', 'construction', 'transport', 'service', 'residential']
      
      // 处理数据:计算相对于2020年的变化
      const processedData = sectors.map(sector => {
        // 获取2020年的基准值
        const baseValue = store.state.scenarios[selectedScenario.value][selectedProvince.value].sectors[sector].intensity['2020']
        
        // 计算每年相对于2020年的比值
        const data = years.map(year => {
          const currentValue = store.state.scenarios[selectedScenario.value][selectedProvince.value].sectors[sector].intensity[year]
          return parseFloat((currentValue / baseValue).toFixed(3)) // 转换为数字并保留3位小数
        })
        
        return {
          name: getSectorLabel(sector),
          data: data
        }
      })

      const option = {
        title: { 
          text: '能源强度', 
          left: 'center',
          textStyle: {
            fontSize: 24,
            fontWeight: 'bold'
          }
        },
        tooltip: { 
          trigger: 'axis',
          textStyle: {
            fontSize: 16
          },
          formatter: function(params) {
            let result = `${params[0].axisValue}年<br/>`
            params.forEach(param => {
              // 显示相对变化倍数
              result += `${param.seriesName}: ${param.value}倍<br/>`
            })
            return result
          }
        },
        legend: {
          bottom: 0,
          type: 'scroll',
          itemGap: 20,
          textStyle: {
            fontSize: 20
          },
          orient: 'horizontal',
          width: '90%',
          height: 30
        },
        grid: {
          top: 60,
          bottom: 40,
          left: 60,
          right: 60,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: years,
          name: '',
          nameTextStyle: {
            fontSize: 20
          },
          axisLabel: {
            fontSize: 20,
            margin: 8,
            interval: 0
          }
        },
        yAxis: {
          type: 'value',
          name: '（2020=1）',
          nameTextStyle: {
            fontSize: 20
          },
          axisLabel: {
            fontSize: 20,
            formatter: '{value}'
          }
        },
        series: processedData.map(item => ({
          name: item.name,
          type: chartType.value,
          data: item.data,
          smooth: true,  // 使线条更平滑
          symbolSize: 8, // 数据点大小
          lineStyle: {
            width: 3  // 线条宽度
          }
        }))
      }
      
      intensityChart?.setOption(option)
    }

    const updateElectrificationChart = () => {
      const option = {
        title: { 
          text: '电气化率', 
          left: 'center',
          textStyle: {
            fontSize: 24,
            fontWeight: 'bold'
          }
        },
        tooltip: { 
          trigger: 'axis',
          textStyle: {
            fontSize: 16
          }
        },
        legend: {
          bottom: 0,
          type: 'scroll',
          itemGap: 20,
          textStyle: {
            fontSize: 20
          },
          orient: 'horizontal',
          width: '90%',
          height: 30
        },
        grid: {
          top: 60,
          bottom: 60,
          left: 60,
          right: 60,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: years,
          name: '',
          nameTextStyle: {
            fontSize: 20
          },
          axisLabel: {
            fontSize: 20,
            margin: 8,
            interval: 0
          }
        },
        yAxis: {
          type: 'value',
          name: '(%)     ',
          nameTextStyle: {
            fontSize: 20
          },
          axisLabel: {
            fontSize: 20
          }
        },
        series: ['agriculture', 'industry', 'construction', 'transport', 'service', 'residential'].map(sector => ({
          name: getSectorLabel(sector),
          type: chartType.value,
          data: years.map(year => 
            store.state.scenarios[selectedScenario.value][selectedProvince.value].sectors[sector].electrification[year] || 0
          )
        }))
      }
      electrificationChart?.setOption(option)
    }

    const updateHydrogenationChart = () => {
      const option = {
        title: { 
          text: '氢能化率', 
          left: 'center',
          textStyle: {
            fontSize: 24,
            fontWeight: 'bold'
          }
        },
        tooltip: { 
          trigger: 'axis',
          textStyle: {
            fontSize: 16
          }
        },
        legend: {
          bottom: 0,
          type: 'scroll',
          itemGap: 20,
          textStyle: {
            fontSize: 20
          },
          orient: 'horizontal',
          width: '90%',
          height: 30
        },
        grid: {
          top: 60,
          bottom: 60,
          left: 60,
          right: 60,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: years,
          name: '',
          nameTextStyle: {
            fontSize: 20
          },
          axisLabel: {
            fontSize: 20,
            margin: 8,
            interval: 0
          }
        },
        yAxis: {
          type: 'value',
          name: '(%)     ',
          nameTextStyle: {
            fontSize: 20
          },
          axisLabel: {
            fontSize: 20
          }
        },
        series: ['agriculture', 'industry', 'construction', 'transport', 'service', 'residential'].map(sector => ({
          name: getSectorLabel(sector),
          type: chartType.value,
          data: years.map(year => 
            store.state.scenarios[selectedScenario.value][selectedProvince.value].sectors[sector].hydrogenation[year] || 0
          )
        }))
      }
      hydrogenationChart?.setOption(option)
    }

    // 添加 chartType 到监听列表
    watch([selectedScenario, selectedProvince, chartType], () => {
      nextTick(() => {
        updateAllCharts()
      })
    })

    // 组件挂载时初始化
    onMounted(() => {
      nextTick(() => {
        initCharts()
        updateAllCharts()
      })
    })

    // 修改公共图表配置
    const commonChartConfig = {
      title: {
        textStyle: {
          fontSize: 26,  // 增大标题字体
          fontWeight: 'bold'
        },
        top: 5
      },
      tooltip: {
        textStyle: {
          fontSize: 20  // 增大提示框字体
        }
      },
      legend: {
        bottom: 5,
        textStyle: {
          fontSize: 16,
          lineHeight: 16
        },
        itemGap: 25,
        itemWidth: 25,
        itemHeight: 14,
        orient: 'horizontal',
        width: '90%',
        height: 50,  // 为两行图例预留空间
        formatter: function(name) {
          return name.length > 3 ? name.substr(0, 3) + '\n' + name.substr(3) : name;
        }
      },
      grid: {
        top: 50,
        bottom: 80,  // 保持足够的底部空间给图例
        left: 60,
        right: 60,
        containLabel: true
      },
      xAxis: {
        nameTextStyle: {
          fontSize: 20  // 增大坐标轴名称字体
        },
        axisLabel: {
          fontSize: 18,  // 增大坐标轴标签字体
          margin: 8
        },
        axisLine: {
          lineStyle: {
            width: 2  // 加粗坐标轴线
          }
        }
      },
      yAxis: {
        nameTextStyle: {
          fontSize: 20
        },
        axisLabel: {
          fontSize: 18,
          margin: 8
        },
        axisLine: {
          lineStyle: {
            width: 2
          }
        }
      },
      series: {
        lineStyle: {
          width: 4  // 加粗线条
        },
        itemStyle: {
          borderWidth: 3  // 增大数据点大小
        }
      }
    }

    // 修改多部门图表的图例配置
    const multiSectorLegendConfig = {
      ...commonChartConfig.legend,
      orient: 'horizontal',
      width: '95%',
      height: 70,  // 增加高度以适应更大的字体
      type: 'scroll',
      formatter: function(name) {
        return name.length > 4 ? name.substr(0, 4) + '\n' + name.substr(4) : name;
      },
      textStyle: {
        fontSize: 18,  // 增大图例文字
        lineHeight: 18
      },
      itemGap: 25,
      bottom: 5
    }

    // 添加窗口大小变化监听
    window.addEventListener('resize', () => {
      nextTick(() => {
        if (popGdpChart) popGdpChart.resize()
        if (intensityChart) intensityChart.resize()
        if (electrificationChart) electrificationChart.resize()
        if (hydrogenationChart) hydrogenationChart.resize()
      })
    })

    // 在 setup 函数中添加新的 ref
    const industryStructureChartRef = ref(null)
    let industryStructureChart = null

    // 在 initCharts 函数中添加新图表初始化
    const initCharts = () => {
      if (popGdpChartRef.value) {
        popGdpChart = echarts.init(popGdpChartRef.value)
      }
      if (intensityChartRef.value) {
        intensityChart = echarts.init(intensityChartRef.value)
      }
      if (electrificationChartRef.value) {
        electrificationChart = echarts.init(electrificationChartRef.value)
      }
      if (hydrogenationChartRef.value) {
        hydrogenationChart = echarts.init(hydrogenationChartRef.value)
      }
      if (industryStructureChartRef.value) {
        industryStructureChart = echarts.init(industryStructureChartRef.value)
      }
      
      window.addEventListener('resize', () => {
        popGdpChart?.resize()
        intensityChart?.resize()
        electrificationChart?.resize()
        hydrogenationChart?.resize()
        industryStructureChart?.resize()
      })
    }

    // 添加更新产业结构图表的函数
    const updateIndustryStructureChart = () => {
      if (!industryStructureChart || !selectedProvince.value || !selectedScenario.value) return

      const data = store.state.scenarios[selectedScenario.value][selectedProvince.value]
      if (!data) return

      const years = ['2020', '2025', '2030', '2035', '2040', '2045', '2050', '2055', '2060']
      
      // 计算每年的产业结构比例
      const primaryData = []
      const secondaryData = []
      const tertiaryData = []
      
      years.forEach(year => {
        // 修改数据访问路径
        const primary = data.gdp_composition.primary[year] || 0
        const secondary = data.gdp_composition.secondary[year] || 0
        const tertiary = data.gdp_composition.tertiary[year] || 0
        
        const total = primary + secondary + tertiary
        if (total === 0) {
          primaryData.push(0)
          secondaryData.push(0)
          tertiaryData.push(0)
        } else {
          primaryData.push(((primary / total) * 100).toFixed(2))
          secondaryData.push(((secondary / total) * 100).toFixed(2))
          tertiaryData.push(((tertiary / total) * 100).toFixed(2))
        }
      })

      const option = {
        title: { 
          text: '三产结构', 
          left: 'center',
          textStyle: {
            fontSize: 24,
            fontWeight: 'bold'
          }
        },
        tooltip: { 
          trigger: 'axis',
          textStyle: {
            fontSize: 16
          },
          formatter: function(params) {
            let result = `${params[0].axisValue}年<br/>`
            params.forEach(param => {
              result += `${param.seriesName}: ${param.value}%<br/>`
            })
            return result
          }
        },
        legend: {
          bottom: 0,
          textStyle: {
            fontSize: 20
          },
          data: ['第一产业', '第二产业', '第三产业']
        },
        grid: {
          top: 60,
          bottom: 60,
          left: 60,
          right: 60,
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: years,
          name: '',
          nameTextStyle: {
            fontSize: 20
          },
          axisLabel: {
            fontSize: 20,
            margin: 8,
            interval: 0
          }
        },
        yAxis: {
          type: 'value',
          name: '(%)      ',
          nameTextStyle: {
            fontSize: 20
          },
          axisLabel: {
            fontSize: 20
          },
          max: 100,
          min: 0
        },
        series: [
          {
            name: '第一产业',
            type: chartType.value,
            data: primaryData,
            itemStyle: {
              color: '#91CC75'
            }
          },
          {
            name: '第二产业',
            type: chartType.value,
            data: secondaryData,
            itemStyle: {
              color: '#5470C6'
            }
          },
          {
            name: '第三产业',
            type: chartType.value,
            data: tertiaryData,
            itemStyle: {
              color: '#FAC858'
            }
          }
        ]
      }
      
      industryStructureChart?.setOption(option)
    }

    return {
      selectedScenario,
      selectedProvince,
      selectedYear,
      selectedVariable,
      chartType,
      showScenarioCreate,
      newScenarioName,
      currentData,
      provinces,
      years,
      customScenarios,
      createScenario,
      deleteScenario,
      handleDataChange,
      handleSectorChange,
      saveChanges,
      getSectorLabel,
      chartRef,
      updateChart,
      popGdpChartRef,
      intensityChartRef,
      electrificationChartRef,
      hydrogenationChartRef,
      industryStructureChartRef,
      // 添加文件上传相关变量和方法
      fileInput,
      isUploading,
      uploadStatus,
      triggerFileUpload,
      handleFileUpload,
    }
  }
}
</script>

<style scoped>
.demand-view {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%);
}

.container {
  min-height: calc(100vh - 60px);
  padding: 20px 32px;
  max-width: 2000px;
  margin: 0 auto;
}

.main-content {
  display: grid;
  grid-template-columns: 1fr 1fr;  /* 左右等宽 */
  gap: 24px;
  padding: 16px 0;
  min-height: 100%;
}

/* 卡片基础样式 */
.card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  margin-bottom: 20px;
}

.card:last-child {
  margin-bottom: 0;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  background: linear-gradient(to right, #f8fafc, #ffffff);
  border-bottom: 1px solid #edf2f7;
}

.card-header i {
  font-size: 32px;
  color: #1890ff;
}

.card-header h3 {
  font-size: 28px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.card-body {
  padding: 20px 24px;
}

/* 表单元素样式 */
.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 15px;
  font-size: 20px;
  font-weight: 500;
  color: #1e4c6b;
}

.form-select, .form-input {
  width: 100%;
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 20px;
  color: #4a5568;
  background-color: white;
  transition: all 0.2s ease;
}

.form-select:hover, .form-input:hover {
  border-color: #63b3ed;
}

.form-select:focus, .form-input:focus {
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
}

/* 按钮样式 */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 20px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn i {
  font-size: 22px;
}

.btn-primary {
  background: linear-gradient(135deg, #1890ff, #096dd9);
  color: white;
  border: none;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #40a9ff, #1890ff);
  transform: translateY(-1px);
}

.btn-danger {
  background: linear-gradient(135deg, #ff4d4f, #cf1322);
  color: white;
  border: none;
}

.btn-success {
  background: linear-gradient(135deg, #48bb78, #38a169);
  color: white;
  border: none;
}

.btn-success:hover {
  background: linear-gradient(135deg, #38a169, #2f855a);
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(56, 161, 105, 0.3);
}

.btn-text {
  background: transparent;
  color: #1890ff;
  border: 1px solid #1890ff;
}

.btn-text:hover {
  background: rgba(24, 144, 255, 0.1);
}

/* 数据编辑区域 */
.data-section {
  height: auto;
  gap: 16px;
  padding: 16px;
}

.data-section h4 {
  color: #1e4c6b;
  margin-bottom: 20px;
  font-size: 20px;
  font-weight: 600;
}

/* 部门组样式 - 包含每个部门(工业、建筑等)的参数输入区域 */
.sector-group {
  padding: 32px;                    /* 内边距 */
  background: #f8fafc;              /* 浅灰色背景 */
  border-radius: 8px;               /* 圆角边框 */
  border: 1px solid #e2e8f0;        /* 浅灰色边框 */
  transition: all 0.2s ease;        /* 过渡动画效果 */
  margin-bottom: 24px;              /* 底部外边距,用于部门组之间的间隔 */
}

/* 部门组悬停效果 */
.sector-group:hover {
  background: #fff;                 /* 悬停时背景变为白色 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);  /* 添加阴影效果 */
}

/* 部门标题区域样式 */
.sector-header {
  margin-bottom: 12px;              /* 底部外边距 */
  padding-bottom: 10px;              /* 底部内边距 */
  border-bottom: 1px solid #edf2f7; /* 底部分隔线 */
}

.sector-header h5 {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #edf2f7;
  color: #2d3748;
}

.sector-inputs {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-group {
  display: grid;
  /* 三列布局:
      90px - 标签列宽度
      130px - 输入框列宽度
      150px - 增加单位列宽度以适应较长的单位文本 */
  grid-template-columns: 90px 130px 150px;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
}

.input-group label {
  font-size: 20px;
  color: #4a5568;
  white-space: nowrap;
}

.input-group .unit {
  font-size: 20px;
  color: #718096;
  white-space: nowrap;
}

.parameters-row {
  display: flex;
  gap: 12px;
  padding: 15px;
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 20px;
}

.parameter-group {
  flex: 1;
  min-width: 0;
}

/* 图表区域 */
.chart-container {
  min-height: calc(100% - 70px);
  height: 800px;
  padding: 16px;
}

.chart-controls {
  display: flex;
  gap: 12px;
  padding: 16px 24px;
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 16px;
}

.chart-controls .form-select {
  min-width: 180px;
  font-size: 20px;
  padding: 12px 16px;
}

/* 通知提示 */
.baseline-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: linear-gradient(135deg, #e6f7ff, #bae7ff);
  border-radius: 8px;
  color: #1890ff;
  margin-bottom: 20px;
}

.baseline-notice i {
  font-size: 18px;
}

/* 动画效果 */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 响应式设计 */
@media (max-width: 1400px) {
  .main-content {
    grid-template-columns: 1fr 1fr;  /* 保持等宽 */
  }
}

@media (max-width: 1200px) {
  .main-content {
    grid-template-columns: 1fr;  /* 小屏幕堆叠显示 */
    gap: 20px;
  }
  
  .fixed-height {
    min-height: 1200px;  /* 调整堆叠时的高度 */
  }
}

@media (max-width: 768px) {
  .container {
    padding: 10px;
  }
  
  .chart-controls {
    flex-direction: column;
  }
  
  .form-select, .form-input {
    font-size: 18px;
    padding: 12px 16px;
  }
  
  /* 
   * 定义输入组的网格布局:
   * 90px - 左侧固定宽度90像素
   * 1fr - 中间部分自适应占满剩余空间
   * 150px - 右侧固定宽度150像素
   */
  .input-group {
    grid-template-columns: 60px 1fr 150px;
  }
  
  .card-header h3 {
    font-size: 22px;
  }
}

/* 禁用状态样式 */
.form-select:disabled,
.form-input:disabled {
  background-color: #f7fafc;
  border-color: #edf2f7;
  color: #a0aec0;
  cursor: not-allowed;
}

/* 左侧滚动面板 */
.scroll-container {
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 16px;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
}

.scroll-container::-webkit-scrollbar {
  width: 4px;
}

.scroll-container::-webkit-scrollbar-track {
  background: #f7fafc;
}

.scroll-container::-webkit-scrollbar-thumb {
  background-color: #cbd5e0;
  border-radius: 2px;
}

/* 右侧固定高度面板 */
.fixed-height {
  min-height: 1300px;  /* 适应4个垂直排列的图表 */
  display: flex;
  flex-direction: column;
}

/* 图表卡片样式 */
.chart-card {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.chart-card .card-body {
  flex: 1;
  padding: 0;
}

/* 添加新的样式 */
.basic-data-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 20px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 8px;
}

.sectors-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-top: 12px;
}

.sectors-column {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* 图表网格布局 */
.charts-grid {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: repeat(5, 1fr);
  gap: 20px;
  padding: 20px;
  height: 100%;
}

.chart-item {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  padding: 15px;
}

.chart-container {
  height: 100%;
  min-height: 280px;  /* 每个图表的最小高度 */
}

/* 控制组样式 */
.chart-controls {
  display: flex;
  gap: 20px;
  padding: 16px 24px;
}

.control-group {
  flex: 1;
}

.control-group .form-select {
  width: 100%;
}

/* 产业增加值样式 */
.industry-increment {
  margin-bottom: 20px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 8px;
}

.increment-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

/* 增长率标签样式 */
.increment-label {
  font-size: 22px;  /* 标签文字大小 */
  color: #1e4c6b;   /* 标签文字颜色-深蓝色 */
  white-space: nowrap;
  min-width: 100px;
}

/* 输入框容器样式 */
.increment-inputs {
  display: flex;
  gap: 16px;
  flex: 1;
}

/* 输入框组样式 */
.increment-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

/* 输入框标签样式 */
.increment-input-group label {
  font-size: 22px;  /* 输入框标签文字大小 */
  color: #4a5568;   /* 输入框标签文字颜色-灰色 */
  white-space: nowrap;
}

/* 输入框样式 */
.increment-input-group .form-input {
  width: 100%;
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 20px;  /* 输入框文字大小 */
  color: #4a5568;   /* 输入框文字颜色-灰色 */
  background-color: white;
  transition: all 0.2s ease;
}

/* 输入框悬停状态 */
.increment-input-group .form-input:hover {
  border-color: #63b3ed;  /* 边框颜色-浅蓝色 */
}

/* 输入框聚焦状态 */
.increment-input-group .form-input:focus {
  border-color: #4299e1;  /* 边框颜色-蓝色 */
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.15);
}

/* 输入框禁用状态 */
.increment-input-group .form-input:disabled {
  background-color: #f7fafc;  /* 背景色-浅灰色 */
  border-color: #edf2f7;      /* 边框颜色-浅灰色 */
  color: #a0aec0;            /* 文字颜色-灰色 */
  cursor: not-allowed;
}

.upload-btn-container {
  margin-left: auto;
}

.upload-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #4caf50, #2e7d32);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;
}

.upload-btn:hover {
  background: linear-gradient(135deg, #5cb860, #388e3c);
  transform: translateY(-2px);
}

.upload-btn i {
  font-size: 16px;
}
</style> 